import { useRef, useState } from "react";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

// Before the first save of a draft: warn if a live eBay listing looks like the same item. Returns [guard(draft) → Promise<bool>, dialog].
export function useDupGuard() {
  const checked = useRef(new Map());
  const resolver = useRef(null);
  const [matches, setMatches] = useState(null);
  const guard = (draft) => {
    const title = (draft?.title || "").trim();
    const key = `${draft?.id}:${title.toLowerCase()}`;
    if (title.length < 3 || draft?.ebay?.listing_id) return Promise.resolve(true);
    if (checked.current.has(key)) return checked.current.get(key) === true ? Promise.resolve(true) : Promise.resolve(false);
    const p = (async () => {
      let found = [];
      try {
        const { data } = await api.post("/inventory/dup-check", { title: title.slice(0, 200), exclude_id: draft.id || "" });
        found = (data.matches || []).filter((m) => m.source === "ebay");
      } catch { checked.current.set(key, true); return true; }
      if (!found.length) { checked.current.set(key, true); return true; }
      setMatches(found);
      const ok = await new Promise((res) => { resolver.current = res; });
      checked.current.set(key, true);
      return ok;
    })();
    checked.current.set(key, "pending");
    return p;
  };
  const answer = (ok) => { setMatches(null); resolver.current?.(ok); resolver.current = null; };
  const dialog = (
    <Dialog open={!!matches} onOpenChange={(o) => !o && answer(false)}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="dup-warning-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Already live on eBay?</DialogTitle>
          <DialogDescription>This looks like something you already have listed. Check before you save a second listing.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-slate-100" data-testid="dup-warning-matches">
          {(matches || []).map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-2.5" data-testid={`dup-warning-match-${m.id}`}>
              {m.image_url ? <img src={m.image_url} alt="" className="h-12 w-12 shrink-0 rounded-md border border-slate-200 object-cover" /> : <span className="h-12 w-12 shrink-0 rounded-md bg-slate-100" />}
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-medium text-slate-900">{m.title}</p>
                <p className="mt-0.5 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                  {m.sku && <span className="rounded bg-slate-900 px-1.5 font-mono font-bold text-white">SKU {m.sku}</span>}
                  {m.price > 0 && <span>${Number(m.price).toFixed(2)}</span>}<span>· qty {m.quantity}</span>{m.bin && <span>· {m.bin}</span>}
                </p>
              </div>
              {m.url && <a href={m.url} target="_blank" rel="noreferrer" aria-label="Open on eBay" className="p-1 text-slate-400 hover:text-[#0053DA]"><ExternalLink className="h-4 w-4" /></a>}
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => answer(false)} data-testid="dup-warning-cancel">Cancel</Button>
          <Button onClick={() => answer(true)} className="bg-[#0053DA] hover:bg-[#0042ad]" data-testid="dup-warning-continue">Save anyway</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
  return [guard, dialog];
}
